import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, X, ChartLineUp, GameController, Newspaper, Trophy, Rocket } from '@phosphor-icons/react';
import { Button } from './ui/button';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const SLIDES = [
  {
    icon: ChartLineUp,
    color: 'from-emerald-500 to-teal-600',
    title: 'Welcome to TeenVest 🎉',
    body: 'You start with $25,000 virtual money to trade real stocks, crypto, ETFs, and more. Real prices, no real risk. Practice like the pros.',
    points: [
      'Buy low, sell high — same as real markets',
      'Powered by live yfinance data',
      'No real money required',
    ],
  },
  {
    icon: Newspaper,
    color: 'from-blue-500 to-indigo-600',
    title: 'How the Stock Market Works',
    body: 'Stocks are tiny pieces of real companies. Their prices move with news, earnings, and demand. Smart investors buy great companies and hold them.',
    points: [
      'P/E ratio shows how expensive a stock is',
      'News affects prices in seconds',
      'Long-term holders beat day-traders 9 out of 10 times',
    ],
  },
  {
    icon: GameController,
    color: 'from-orange-500 to-red-600',
    title: 'Game Modes Earn You Money',
    body: 'Play 6 different modes to earn cash AND XP. Each completed mode gives +200 XP and bonus cash to your portfolio.',
    points: [
      'Chaos Mode — survive market crashes',
      'Oracle Mode — think like Buffett',
      'News Reactor — react to live news',
      'Reach Master rank to unlock 🇮🇳 Indian stocks',
    ],
  },
  {
    icon: Trophy,
    color: 'from-yellow-400 to-yellow-600',
    title: 'Daily Lesson + Quiz',
    body: 'Every day a new investing lesson from Buffett, Graham, and the greats — plus a 5-question quiz. Get all 5 right and earn +$50 cash!',
    points: [
      'Master P/E ratios, balance sheets, moats',
      'Spot bubbles before they pop',
      'Build a habit — 1 minute per day',
    ],
  },
  {
    icon: Rocket,
    color: 'from-purple-500 to-pink-600',
    title: "You're Ready! 🚀",
    body: "That's it! Hit the Market tab to make your first trade. Pro tip: start with companies you know — Apple, Nike, McDonald's. Then check the Game Modes tab to earn extra cash and XP!",
    points: [
      'Step 1: Go to Market → buy any stock',
      'Step 2: Try a Game Mode',
      'Step 3: Climb the leaderboard',
    ],
  },
];

const OnboardingModal = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);
  const { fetchUser } = useAuth();
  const slide = SLIDES[step];
  const isLast = step === SLIDES.length - 1;
  const Icon = slide.icon;

  const finish = async () => {
    setClosing(true);
    try {
      await api.post('/learning/onboarding-complete', { completed: true });
      await fetchUser();
    } catch (e) { /* non-fatal */ }
    onClose && onClose();
  };

  const next = () => {
    if (isLast) finish();
    else setStep(s => s + 1);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
        data-testid="onboarding-modal"
      >
        <motion.div
          key={step}
          initial={{ scale: 0.95, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-xl glass-card overflow-hidden relative"
        >
          <button
            onClick={finish}
            disabled={closing}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-300 z-10"
            data-testid="onboarding-skip"
            aria-label="Skip onboarding"
          >
            <X size={18} />
          </button>

          <div className={`bg-gradient-to-br ${slide.color} p-8 pb-14 relative`}>
            <p className="text-white/80 text-xs font-bold tracking-widest uppercase mb-2">
              Step {step + 1} of {SLIDES.length}
            </p>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-black/30 backdrop-blur mb-3">
              <Icon size={36} weight="fill" className="text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-unbounded font-bold text-white">{slide.title}</h2>
          </div>

          <div className="-mt-8 mx-6 mb-6 glass rounded-2xl p-6">
            <p className="text-zinc-200 mb-4 leading-relaxed">{slide.body}</p>
            <ul className="space-y-2">
              {slide.points.map((pt, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                  <span className="text-gold mt-0.5">•</span>
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 mb-5">
            {SLIDES.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-8 bg-gold' : i < step ? 'w-1.5 bg-gold/60' : 'w-1.5 bg-white/20'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 px-6 pb-6">
            <Button
              variant="ghost"
              onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={step === 0 || closing}
              className="text-zinc-400 hover:text-white"
              data-testid="onboarding-back"
            >
              <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
            <Button
              onClick={next}
              disabled={closing}
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full px-6"
              data-testid={isLast ? 'onboarding-finish' : 'onboarding-next'}
            >
              {isLast ? "Let's Go! 🚀" : 'Next'} {!isLast && <ArrowRight size={16} className="ml-1" />}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingModal;
